import React, { Suspense, lazy, useEffect } from "react";
import useMedia from "use-media";
import { useSelector } from "react-redux";
import { selectAppVisible } from "reducers/auth";
import { Router, Location, RouteComponentProps, Redirect } from "@reach/router";
import ErrorBoundary from "components/ErrorBoundary";
import { useCheckSignedIn } from "helpers/useCheckSignedIn";
import ErrorFallback from "components/fallbacks/ErrorFallback";
import Page from "components/Page";
import "react-toastify/dist/ReactToastify.css";
import { MobileHeader } from "components/navigation/MobileHeader";
import Navigation from "components/navigation/Navigation";
import { useSignOutAcrossTabs } from "helpers/useSignOutAcrossTabs";
import { Toasts } from "components/Toasts";
import { registerUserLocale } from "helpers/userLocale";
import { Spinner } from "components/Spinner/Spinner";
import MainHeader from "components/navigation/MainHeader";
import CashierUsers from "pages/cashierUsers";
import PendingGuarantees from "pages/pendingGuarantees";
import ApprovedGuarantees from "pages/approvedGuarantees";
import LockedGuarantees from "pages/lockedGuarantees";
import DeclinedGuarantees from "pages/declinedGuarantees";
import Managers from "pages/managers";
import Footer from "components/Footer/Footer";
import Dashboard from "pages/dashboard";
import GuaranteeHistorys from "pages/guaranteeHistorys";
import ApproveModal from "components/helpers/ApproveModal/ApproveModal";
import ComingSoon from "pages/ComingSoon";
import CashierActions from "pages/cashierActions";
import Account from "pages/Account";
import Reports from "pages/reports";
import Analytics from "pages/analytics";
import { PdfReportPage } from "pages/pdfReports/PdfReport";
import { PdfHeaderPage } from "pages/pdfReports/PdfHeaderPage";
import { PdfFooterPage } from "pages/pdfReports/PdfFooterPage";
import { AutoLogin } from "pages/AutoLogin";
import Register from "pages/registration";
import { Slide, ToastContainer, toast } from "react-toastify";
import { GlobalModal } from "pages/Onboarding/Modals/GlobalModal";
import { useCheckVerification } from "helpers/useCheckVerifiction";
import ViewProfile from "pages/viewProfile/ViewProfile";
import { SignificantPartyJumio } from "pages/jumio/SignificantPartyJumio";
import { useHandleSignalRNotifications } from "components/helpers/useHandleSignalRNotifications";
import Session from "pages/session/Session";
import Customers from "pages/Customers";
import CustomerTransfers from "pages/CustomerTransfers";

// import { OnboardingModal } from "pages/Onboarding/OnBoardingModal";

/** Standard Components */
const SignIn = lazy(() => import("components/SignIn/SignIn"));
const ForgottenPassword = lazy(() =>
  import("components/ForgottenPassword/ForgottenPassword")
);
const ResetPassword = lazy(() =>
  import("components/ResetPassword/ResetPassword")
);
const SetPassword = lazy(() => import("components/SetPassword/SetPassword"));
const NotFound = lazy(() => import("pages/NotFound"));
const Home = lazy(() => import("pages/Home"));

const NotFoundPage = (props: RouteComponentProps) => (
  <Page isPublic>
    <NotFound {...props} />
  </Page>
);
const ComingSoonPage = (props: RouteComponentProps) => (
  <Page>
    <ComingSoon {...props} />
  </Page>
);
const SetPasswordPage = (props: RouteComponentProps) => (
  <Page isPublic>
    <SetPassword {...props} />
  </Page>
);

const AutoLoginPage = (props: RouteComponentProps) => (
  <Page isPublic>
    <AutoLogin {...props} />
  </Page>
);

const ResetPasswordPage = (props: RouteComponentProps) => (
  <Page isPublic>
    <ResetPassword location={props.location!} />
  </Page>
);

const HomePage = (props: RouteComponentProps) => (
  <Page>
    <Home {...props} />
  </Page>
);

const ForgottenPasswordPage = (props: RouteComponentProps) => (
  <Page isPublic>
    <ForgottenPassword {...props} />
  </Page>
);

// Array of strings represeting the paths where nav should be hidden.
const HIDE_NAV_ON_PATHS: string[] = ["/templates/"];
const HIDE_HEADER_ON_PATHS: string[] = ["/templates/"];

/* Main App */
const App: React.FC<RouteComponentProps> = ({ location }) => {
  registerUserLocale();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const isSignedIn = useCheckSignedIn();
  useCheckVerification(isSignedIn);
  const isAppVisible = useSelector(selectAppVisible);

  const tablet = useMedia({ maxWidth: 1200 });

  useEffect(() => {
    !tablet && setMobileNavOpen(false);
  }, [tablet]);

  const pathname = location?.pathname;
  const currentLocationIncludes = (locationStrings: string[]): boolean => {
    if (pathname) {
      for (let i = 0; i < locationStrings.length; i++) {
        const current = locationStrings[i];
        if (pathname.includes(current)) {
          return true;
        }
      }
    }

    return false;
  };
  const navHidden = !isSignedIn || currentLocationIncludes(HIDE_NAV_ON_PATHS);
  const headerHidden = currentLocationIncludes(HIDE_HEADER_ON_PATHS);

  // If we sign out here, we should sign out in other open windows.
  useSignOutAcrossTabs();

  // Signal R:
  useHandleSignalRNotifications();

  // At startup, we check to see if we should display a signin page or the content.
  // Return nothing, so that we don't flash a view of signin if unneccessary.
  if (!isAppVisible) return null;

  const openMobileNav = () => {
    setMobileNavOpen(true);
  };

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<Spinner positionAbsolute />}>
        <Location>
          {({ location }) => (
            <Navigation
              location={location}
              closeMobileNav={() => setMobileNavOpen(false)}
              mobileNavOpen={mobileNavOpen}
              hide={navHidden}
            />
          )}
        </Location>
        <div
          className={`Main ${navHidden ? "NoNav " : ""}${
            headerHidden ? "NoHeader " : ""
          } `}
        >
          {!headerHidden &&
            (tablet ? (
              <MobileHeader
                setMobileNavOpen={setMobileNavOpen}
                isMobileNavOpen={mobileNavOpen}
              />
            ) : (
              <MainHeader />
            ))}

          <Router>
            <HomePage path="/" />
            <SignificantPartyJumio path="/idverification" />
            <Register path="/register" />
            <SignIn path="/sign-in" />
            <Session path="/create-session" />
            <ViewProfile path="/view-profile" />
            <ForgottenPasswordPage path="/forgotten-password" />
            <ResetPasswordPage path="/reset-password" />
            <SetPasswordPage path="/set-password" />
            <Dashboard path="/dashboard" />
            <CashierActions path="/cashieractions" />
            <CashierUsers path="/cashiers" />
            <PendingGuarantees path="/pendingguarantees" />
            <ApprovedGuarantees path="/approvedguarantees" />
            <LockedGuarantees path="/lockedguarantees" />
            <DeclinedGuarantees path="/declinedguarantees" />
            <GuaranteeHistorys path="/guaranteeshistory" />
            <Customers path="/customerusers" />
            <CustomerTransfers path="/customertransfers/:id" />
            <Managers path="/managers" />
            <Account path="/account" />
            <Reports path="/reports" />
            <Analytics path="/analytics/*" />
            <NotFoundPage default />
            <PdfReportPage path="/templates/report" />
            <PdfHeaderPage path="/templates/header" />
            <PdfFooterPage path="/templates/footer" />
            <AutoLoginPage path="/autologin" />
          </Router>
          {!headerHidden && <Footer />}
        </div>
      </Suspense>
      <GlobalModal />
      <ToastContainer
        position={toast.POSITION.BOTTOM_CENTER}
        className="ToastMessage"
        autoClose={5000}
        transition={Slide}
        icon={false}
        theme={"dark"}
        hideProgressBar
        pauseOnHover
      />

      {/* <Toasts /> */}
    </ErrorBoundary>
  );
};

export default App;
